import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import * as yup from "yup";
import trackError from "src/lib/track-error";
import visaLogo from "src/assets/images/icons/payment-icon-visa.svg";
import mastercardLogo from "src/assets/images/icons/payment-icon-mastercard.svg";
import { FaLock } from "react-icons/fa";
import { color } from "src/styles/variables";
import { ErrorMessage } from "src/react/components/error-message";
import { HelpEmail } from "src/react/components/help-email";

const ADYEN_ENV =
  process.env.REACT_APP_RELEASE_STAGE === "production" ? "live" : "test";

const paymentMethodsResponse = {
  groups: [{ name: "Credit Card", types: ["visa", "mc"] }],
  paymentMethods: [
    {
      details: [
        { key: "encryptedCardNumber", type: "cardToken" },
        { key: "encryptedSecurityCode", type: "cardToken" },
        { key: "encryptedExpiryMonth", type: "cardToken" },
        { key: "encryptedExpiryYear", type: "cardToken" },
        { key: "holderName", optional: true, type: "text" },
      ],
      name: "Credit Card",
      type: "scheme",
    },
  ],
};

const configuration = {
  locale: "en_GB",
  environment: ADYEN_ENV,
  originKey: process.env.REACT_APP_ADYEN_ORIGIN_KEY,
  paymentMethodsResponse: paymentMethodsResponse,
};

const yupString = yup.string().default("").trim();

export const paymentCardSchema = {
  encryptedCardNumber: yupString
    .label("Card number")
    .required()
    .matches(/^((?!invalid).)*$/, "Invalid Card number"),
  encryptedExpiryMonth: yupString
    .label("Card expiry month")
    .required()
    .matches(/^((?!invalid).)*$/, "Invalid Card expiry month"),
  encryptedExpiryYear: yupString
    .label("Card expiry year")
    .required()
    .matches(/^((?!invalid).)*$/, "Invalid Card expiry year"),
  encryptedSecurityCode: yupString
    .label("Card security code")
    .required()
    .matches(/^((?!invalid).)*$/, "Invalid Card security code"),
  cardBrand: yupString
    .label("Card brand")
    .required()
    .matches(
      /^((visa)|(mc)|(card))$/,
      "Sorry, we only accept Visa or Mastercard"
    ),
};

function loadAdyenLibary() {
  return new Promise((resolve) => {
    const stylesId = "adyen-styles";
    const scriptId = "adyen-script";
    const existingStyles = document.getElementById(stylesId);
    const existingScript =
      document.getElementById(scriptId) && window.AdyenCheckout;

    if (existingScript) {
      return resolve();
    }

    if (!existingStyles) {
      const link = document.createElement("link");
      link.href = `https://checkoutshopper-${ADYEN_ENV}.adyen.com/checkoutshopper/sdk/3.6.1/adyen.css`;
      link.id = stylesId;
      link.rel = "stylesheet";
      link.crossorigin = "anonymous";
      document.head.appendChild(link);
    }

    if (!existingScript) {
      const script = document.createElement("script");
      script.src = `https://checkoutshopper-${ADYEN_ENV}.adyen.com/checkoutshopper/sdk/3.6.1/adyen.js`;
      script.id = scriptId;
      script.crossorigin = "anonymous";
      script.async = true;
      script.onload = function () {
        const checkForAdyenLibraryInterval = setInterval(() => {
          if (window.AdyenCheckout) {
            clearInterval(checkForAdyenLibraryInterval);
            resolve();
          }
        }, 200);
      };
      script.onerror = function () {
        resolve();
      };
      document.body.appendChild(script);
    }
  });
}

function PaymentCard({ setFieldValue, ...props }) {
  const [hasError, setHasError] = useState(false);
  const [isLibraryLoaded, setIsLibraryLoaded] = useState(false);

  useEffect(() => {
    let didCancel = false;
    loadAdyenLibary().then(() => {
      if (!didCancel) {
        setIsLibraryLoaded(true);
      }
    });

    return () => {
      didCancel = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isLibraryLoaded) {
      let card;
      try {
        const checkout = new window.AdyenCheckout({
          ...configuration,
          onFieldValid: (field) => {
            const fieldValue = field.valid ? field.blob : "invalid";
            setFieldValue(field.encryptedFieldName, fieldValue);
          },
          onBrand: ({ brand }) => {
            setFieldValue("cardBrand", brand);
          },
        });
        card = checkout.create("card");
        card.mount("#card");
      } catch (error) {
        setHasError(true);
        trackError({ errorMessage: "AdyenCheckout error", error });
      }
      return () => {
        try {
          card.unmount();
        } catch (error) {
          trackError({ errorMessage: "AdyenCheckout error", error });
        }
      };
    }
  }, [setFieldValue, isLibraryLoaded]);

  return (
    <section>
      {hasError ? (
        <ErrorMessage>
          We're sorry, it looks like something has gone wrong... Please refresh
          the page to try again or <HelpEmail>contact help</HelpEmail>.
        </ErrorMessage>
      ) : (
        <CardWrapper id="card" {...props} />
      )}
      <PaymentIcons>
        <PaymentIcon src={visaLogo} alt="Visa" />
        <PaymentIcon src={mastercardLogo} alt="Mastercard" />
        <Padlock size={15} />
      </PaymentIcons>
    </section>
  );
}

PaymentCard.propTypes = {
  setFieldValue: PropTypes.func,
};

export default PaymentCard;

const CardWrapper = styled.div`
  padding: 10px 0 20px;
  border-bottom: 1px solid ${color.darkGrey};
  min-height: 201px;

  .adyen-checkout__input {
    width: 100%;
    border: 2px solid ${color.darkGrey};
    height: 59px;
    background: ${color.white};
  }
`;

const PaymentIcons = styled.div`
  display: flex;
  margin-top: 10px;
  width: 100%;
`;

const PaymentIcon = styled.img`
  width: 26px;
  height: 16px;
  margin-right: 3px;
`;

const Padlock = styled(FaLock)`
  margin-left: auto;
`;
